import {
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Grid,
  Paper,
  Tooltip
} from "@mui/material";
import Viewer from 'react-images-viewer';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { useEffect, useState } from "react";
import useApi from "hooks/useApi";
import { formatDateWithTimezoneDisplay , formatTimestampWithLocalTimezone} from "utils/dateFormat";
import { ResourceDetailsDialog } from "components/ResourceRating";

interface Props {
  open: boolean;
  onClose: () => void;
  child: any;
}

export default function ChildDetailsDialog({ open, onClose, child }: Props) {

  const { getTransactionHistoryDetails } = useApi();

  const [loading, setLoading] = useState(true);
  const [transactionDetails, setTransactionDetails] = useState<any>(null);

  const [imagesPreview, setImagesPreview] = useState<any[]>([]);
  const [currentImage, setCurrentImage] = useState(0);
  const [viewerIsOpen, setViewerIsOpen] = useState(false);
 
  const [videoUrl, setVideoUrl] = useState("");
  const [showVideo, setShowVideo] = useState(false);

  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [resourceDialogData, setResourceDialogData] = useState<any>(null);


  const gotoNext = () => {
    setCurrentImage((prev) => (prev + 1) % imagesPreview.length);
  };
  const gotoPrev = () => {
    setCurrentImage((prev) => (prev - 1 + imagesPreview.length) % imagesPreview.length);
  };
  const closeViewer = () => {
    setViewerIsOpen(false);
  };

  const handleResourceDialogClose = () => {
    setResourceDialogOpen(false);
  };

  useEffect(() => {
    if (!child?.uid || !open) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await getTransactionHistoryDetails(child.uid);
        if (res.success) {
          setTransactionDetails({
            ...res.data,
            history: res.data?.history?.slice().reverse()
          });
        }
      } catch (e) {
        console.error("Child load failed", e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [child, open]);

  const formatDate = (v: string) => formatDateWithTimezoneDisplay(v);
  
  const toSentenceCase = (str: string) =>
            str.charAt(0).toUpperCase() + str.slice(1);

  /* FILE RENDERER */
  const renderFiles = (files: any[]) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
      {files.map((file: any, idx: number) => {
        const ext = file.file_name?.split(".").pop()?.toLowerCase();
        const isImage = ["jpg","jpeg","png","gif","bmp","webp"].includes(ext);
        const isVideo = ext === "mp4";

        return (
          <Box
            key={idx}
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              overflow: "hidden",
              border: "1px solid #e5e7eb",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >

            {isImage || isVideo ? (
              <img
                src={isVideo ? file.url : file.url}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                }}
                onClick={(e) => {
                  e.stopPropagation();

                  if (isVideo) {
                    setVideoUrl(file.url);
                    setShowVideo(true);
                  } else {
                    const images = files
                      .filter((f:any)=>{
                        const e = f.file_name?.split(".").pop()?.toLowerCase();
                        return ["jpg","jpeg","png","gif","bmp","webp"].includes(e);
                      })
                      .map((f:any)=>({ src:f.url }));

                    setImagesPreview(images);
                    setCurrentImage(0);
                    setViewerIsOpen(true);
                  }
                }}
              />
            ) : (<Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.5,
                textAlign: 'center'
              }}
             
            >
             <InsertDriveFileIcon
                sx={{ fontSize: 20, color: "primary.main" }}
                onClick={() => window.open(file.url,"_blank")}
              />
              <Typography
                sx={{
                  fontSize: '6px',
                  color: 'text.secondary',
                  fontWeight: 500,
                  textTransform: 'uppercase'
                }}
              >
                {ext || 'FILE'}
              </Typography>
            </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );

  const renderFieldValue = (field: any) => {

    const val = field?.value?.value;
    const files = field?.value?.files;

    if (files?.length) {
      return renderFiles(files);
    }

    if (field.inputComponent === "SWITCH") {
      const v = Array.isArray(val) ? val[0] : val;
      return (
        <Typography sx={{ fontSize: 12 }}>
          {v === true || v === "true" ? "Yes" : v === false || v === "false" ? "No" : "NA"}
        </Typography>
      );
    }

    if (field.inputComponent === "RESOURCE" && Array.isArray(val)) {

      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {val.map((resource: any, i: number) => (
            <Paper
              key={i}
              sx={{
                p: 0.5,
                border: "1px solid",
                borderColor: "primary.light",
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                cursor: "pointer"
              }}
              onClick={() => {
                setResourceDialogData(resource);
                setResourceDialogOpen(true);
              }}
            >
              <InfoOutlined sx={{ fontSize: 14 }} />

              <Typography sx={{ fontSize: 12 }}>
                {resource?.fields
                  ? Object.values(resource.fields).slice(0,2).join(", ")
                  : "No details"}
              </Typography>
            </Paper>
          ))}
        </Box>
      );
    }

    if (Array.isArray(val)) {

      return val.map((v:any,i:number)=>(
        <Typography key={i} sx={{ fontSize: 12 }}>
          {v
            ? field.inputComponent === "DATE"
              ? formatDate(v)
              : v
            : "NA"}
        </Typography>
      ));
    }

    if (val) {
      return (
        <Typography sx={{ fontSize: 12 }}>
          {field.inputComponent === "DATE"
            ? formatDate(val)
            : val}
        </Typography>
      );
    }

    return (
      <Typography sx={{ fontSize: 12 }}>
        NA
      </Typography>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { maxHeight: "85vh" } }}
    >

      {/* HEADER */}
      <DialogTitle sx={{ p: 1, background: "#132D76" }}>

        <Box display="flex" alignItems="center">
          <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 600 }} > Transaction History </Typography>
        { !loading &&
          <Box ml="auto" display="flex" alignItems="center" gap={1}>

            <Typography fontWeight={600} variant="h6" sx={{ color: '#fff', fontSize: '12px', letterSpacing: '0.025em', textTransform: 'uppercase' }}>
              {transactionDetails?.team?.settings?.transactionLabel || "Txn"}:
            </Typography>
            <Typography
            component="span"
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: "12px",
              px: "10px",
              py: "5px",
              bgcolor: "rgba(255, 255, 255, 0.2)",
              borderRadius: 1,
              border: "1px solid rgba(255, 255, 255, 0.3)",
              textTransform: "uppercase"
            }}
          >
            {transactionDetails?.key}
          </Typography>
            <IconButton size="small" onClick={onClose} sx={{ color:"#fff" }}>
              <CloseIcon fontSize="small" />
            </IconButton>

          </Box>}

        </Box>

      </DialogTitle>

      {/* BODY */}
      <DialogContent dividers sx={{ p: 1.2 }}>
        {loading && (
          <Box textAlign="center" py={3}>
            <CircularProgress size={20} />
            <Typography mt={1} fontSize={12}>
              Loading transaction details...
            </Typography>
          </Box>
        )}

        {!loading && transactionDetails && (

          <Box>
            {/* TRANSACTION INFO */}
            <Box mb={1} sx={{ display:"flex", gap:2, flexWrap:"wrap",  border: "1px solid #e5e7eb", borderRadius: 1, paddingX:1, paddingY:0.4}} >
              <Box>
                <Typography fontSize={11} fontWeight={600}> Status </Typography>
                <Typography fontSize={12}> {transactionDetails.displayStatus || "--"} </Typography>
              </Box>
              <Box>
                <Typography fontSize={11} fontWeight={600}> Reporting Status </Typography>
                <Typography fontSize={12}>  {transactionDetails.displayKeyStatus || "--"} </Typography>
              </Box>
              <Box>
                <Typography fontSize={11} fontWeight={600}> Process </Typography>
                <Typography fontSize={12}> {transactionDetails?.team?.name || "--"} </Typography>
              </Box>
            </Box>

            {/* HISTORY */}
            {transactionDetails?.history?.map((item:any, index:number)=>(

              <Box key={index} mb={1} border="1px solid #e5e7eb" borderRadius={2} >
              {/* STEP HEADER */}
                <Box
                  sx={{
                    background: "#132D76",
                    px: 1,
                    py: 0.4,
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: "13px",
                        fontWeight: 600,
                        letterSpacing: "0.03em",
                        textTransform: "uppercase"
                      }}
                    >
                      {item?.step?.title || "Untitled"}
                    </Typography>
                    {item?.eventType === "REDIRECT" && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.4,
                          bgcolor: "rgba(255,255,255,0.2)",
                          border: "1px solid rgba(255,255,255,0.3)",
                          borderRadius: 0.75,
                          px: 0.6,
                          py: 0.1
                        }}
                      >
                        <CallSplitIcon sx={{ fontSize: 13, color: "#fff", transform: "rotate(90deg)" }} />
                        <Typography sx={{ color: "#fff", fontSize: 10, fontWeight: 700, letterSpacing: "0.05em" }}>
                          REDIRECT
                        </Typography>
                      </Box>
                    )}
                  </Box>

                {/* RIGHT - UPDATED BY + INFO ICON */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

                  {/* UPDATED BY BLOCK */}
                  <Box sx={{ textAlign: "right" }}>
                    {(() => {

                      const updatedName = (() => {
                        if (typeof item?.updatedBy === "object" && item.updatedBy !== null) {
                          if (item.updatedBy?.firstName || item.updatedBy?.lastName)
                            return `${item.updatedBy?.firstName || ""} ${item.updatedBy?.lastName || ""}`.trim();
                          if (item.updatedBy?.name) return item.updatedBy.name;
                        }

                        if (typeof item?.updatedBy === "string") {
                          if (item.updatedBy === item?.user?.id) {
                            if (item?.user?.firstName || item?.user?.lastName)
                              return `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim();
                            if (item?.user?.name) return item.user.name;
                          }
                          return "Updated User";
                        }

                        if (item?.user?.firstName || item?.user?.lastName)
                          return `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim();

                        if (item?.user?.name) return item.user.name;

                        return "";
                      })();

                      const updatedDate = (() => {
                        try {
                          const dateValue =
                            item?.updatedAt ||
                            item?.updated_at ||
                            item?.createdAt ||
                            item?.timestamp ||
                            item?.date ||
                            item?.created_at;

                          return dateValue
                            ? formatTimestampWithLocalTimezone(dateValue)
                            : "";
                        } catch {
                          return "";
                        }
                      })();

                      if (!updatedName && !updatedDate) return null;

                      return (
                        <>
                          <Typography
                            sx={{
                              color: "#fff",
                              fontSize: "12px",
                              letterSpacing: "0.05em"
                            }}
                          >
                            <Box component="span" sx={{fontSize: '0.7rem', color: '#fff', fontWeight: 400, textTransform: 'uppercase', mr: 0.5 }}>
                              UPDATED BY
                            </Box>

                            <Box component="span" sx={{ fontWeight: 600 }}>
                              {updatedName}
                            </Box>
                          </Typography>

                          {updatedDate && (
                            <Typography
                              sx={{
                                color: "#d9e0ff",
                                fontSize: "11px",
                                mt: 0.2
                              }}
                            >
                              {updatedDate}
                            </Typography>
                          )}
                        </>
                      );

                    })()}
                  </Box>

                  {/* INFO ICON */}
                  <Tooltip
                    placement="bottom"
                    arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: 'transparent', boxShadow: 'none',  } }, arrow: { sx: { color: 'transparent' } } }}
                    title={
                      <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.paper', color: 'text.primary', boxShadow: 'none' ,border: '1px solid rgba(0, 0, 0, 0.12)'}}>
                        <Grid container spacing={1.5} sx={{  flexDirection: 'column', display:"flex" }}>
                          <Grid item xs={12}  >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                              <Typography                                                                                               
                                variant="body2"
                                sx={{
                                  fontSize: '0.75rem',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <Box component="span" sx={{ color: 'primary.main', fontWeight: 600, textTransform: 'uppercase' }}>
                                  Created By:{' '}
                                </Box>
                                {(() => {
                                  if (item?.user?.firstName || item?.user?.lastName) {
                                    return `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim();
                                  } else if (item?.user?.name) {
                                    return item.user.name;
                                  } else if (item?.userName) {
                                    return item.userName;
                                  } else if (item?.createdBy?.firstName || item?.createdBy?.lastName) {
                                    return `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim();
                                  } else if (item?.createdBy?.name) {
                                    return item.createdBy.name;
                                  } else {
                                    return 'Not Available';
                                  }
                                })()}
                              </Typography>

                              {(item?.user?.resourceTypeId?.roleName || item?.user?.resourceTypeId?.name || (typeof item?.user?.resourceTypeId === 'string' && item?.user?.resourceTypeId)) && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.7rem',
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    mt: 0.25
                                  }}
                                >
                                  ({item.user.resourceTypeId.name ? item.user.resourceTypeId.name : item.user.resourceTypeId.roleName})
                                </Typography>
                              )}

                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.7rem',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  mt: 0.25
                                }}
                              >
                                {(() => {
                                  try {
                                    const dateValue = item?.createdAt || item?.timestamp || item?.date || item?.created_at;
                                    if (dateValue) return formatTimestampWithLocalTimezone(dateValue);
                                    return 'Not Available';
                                  } catch (error) {
                                    return 'Not Available';
                                  }
                                })()}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={12}>
                            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}>
                              <Typography
                                fontWeight={600}
                                variant="caption"
                                sx={{
                                  color: 'primary.main',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  fontSize: '0.65rem',
                                  mb: 0.5
                                }}
                              >
                                Location
                              </Typography>
                              {(() => {
                                const hasValidCoordinates =
                                  item?.location?.coordinates &&
                                  Array.isArray(item.location.coordinates) &&
                                  item.location.coordinates.length >= 2 &&
                                  !isNaN(Number(item.location.coordinates[0])) &&
                                  !isNaN(Number(item.location.coordinates[1]));

                                if (hasValidCoordinates) {
                                  return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                                      <Box
                                        sx={{
                                          height: 32,
                                          width: 32,
                                          borderRadius: 0.75,
                                          border: '1px solid',
                                          borderColor: 'grey.200',
                                          bgcolor: 'grey.50',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0
                                        }}
                                      >
                                        <LocationOnIcon sx={{ color: 'error.main', fontSize: 16 }} />
                                      </Box>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                          variant="body2"
                                          sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2, mb: 0.25, wordBreak: 'break-word' }}
                                        >
                                          {item.location?.location || item.location?.address || 'Location Available'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1, fontFamily: 'monospace' }}>
                                          {`${Number(item.location.coordinates[0]).toFixed(4)}, ${Number(item.location.coordinates[1]).toFixed(4)}`}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  );
                                } else if (item?.location?.location || item?.location?.address || item?.locationText) {
                                  return (
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500, wordBreak: 'break-word', width: '100%' }}>
                                      {item.location?.location || item.location?.address || item.locationText}
                                    </Typography>
                                  );
                                }
                                return (
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500, fontStyle: 'italic' }}>
                                    Not Available
                                  </Typography>
                                );
                              })()}
                            </Box>
                          </Grid> 
                        </Grid>
                      </Box>
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('Info clicked for item:', item);
                      }}
                      sx={{
                        color: '#fff',
                        bgcolor: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', transform: 'scale(1.05)' },
                        transition: 'all 0.2s ease-in-out'
                      }}
                      title="Info"
                    >
                      <InfoOutlined sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>

                </Box>

              </Box>
           
                {/* FIELDS */}
                <Box sx={{ p:1 }}>
                  <Grid container spacing={1}>

                    {item?.data?.map((field:any,i:number)=>(
                      <Grid item xs={4} key={i}>

                        <Box  sx={{ border:"1px solid #e5e7eb", borderRadius:"6px", p:0.8 }} >
                          <Typography sx={{ fontSize:11, fontWeight:600, mb:0.3 }} >
                            {toSentenceCase(field?.question)}
                          </Typography>
                          {renderFieldValue(field)}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      {showVideo && (
        <Dialog
          open={showVideo}
          onClose={() => setShowVideo(false)}
          maxWidth="xl"
          PaperProps={{ sx: { width: 'fit-content', maxWidth: '100%', m: 2 } }}
        >
          <Box position="relative">
            <IconButton onClick={() => setShowVideo(false)} sx={{ position: 'absolute', top: 8, right: 8, color: 'red', zIndex: 1 }}>
              <HighlightOffIcon fontSize="large" />
            </IconButton>
            <video
              src={videoUrl}
              controls
              autoPlay
              style={{  width: 'auto', maxWidth: '90vw', maxHeight: '80vh', height: 'auto', display: 'block' }}
            />
          </Box>
        </Dialog>
      )}
      <Viewer
        imgs={imagesPreview}
        currImg={currentImage}
        isOpen={viewerIsOpen}
        onClickPrev={gotoPrev}
        onClickNext={gotoNext}
        onClose={closeViewer}
        showCloseBtn
        showImgCount
        backdropCloseable
      />
      <ResourceDetailsDialog
        open={resourceDialogOpen}
        onClose={handleResourceDialogClose}
        resource={resourceDialogData}
        currentTransactionId={child?.uid}
        allowRating={true}
        userId={''}
      />
    </Dialog>
  );
}